export type WidevineRobustness =
  | 'HW_SECURE_ALL'
  | 'HW_SECURE_DECODE'
  | 'HW_SECURE_CRYPTO'
  | 'SW_SECURE_DECODE'
  | 'SW_SECURE_CRYPTO';

export const WIDEVINE_ROBUSTNESS_VALUES: readonly WidevineRobustness[] = [
  'HW_SECURE_ALL',
  'HW_SECURE_DECODE',
  'HW_SECURE_CRYPTO',
  'SW_SECURE_DECODE',
  'SW_SECURE_CRYPTO',
];

export const DEFAULT_MAX_SOFTWARE_WIDEVINE_RESOLUTION = 480;

export function resolveMaxSoftwareWidevineResolution(
  configuredResolution: string | null | undefined,
  sdOnlyForL3: boolean,
): number | null {
  const parsed = configuredResolution
    ? Number.parseInt(configuredResolution, 10)
    : NaN;
  if (Number.isFinite(parsed) && parsed > 0) return parsed;

  return sdOnlyForL3 ? DEFAULT_MAX_SOFTWARE_WIDEVINE_RESOLUTION : null;
}

// Used only when no policy was supplied. Secure decode is deliberately not
// auto-selected on hardware: some devices report support but fail during
// playback. Software secure decode is preferred over software crypto because
// it provides the best available screen-capture protection on an L3 path.
const WIDEVINE_DETECTION_LADDER: readonly WidevineRobustness[] = [
  'HW_SECURE_CRYPTO',
  'SW_SECURE_DECODE',
  'SW_SECURE_CRYPTO',
];

// SD-only-for-L3 is a device-tier policy, so its probe must distinguish a
// hardware path from a software path. The first supported value becomes the
// actual EME request; software results additionally activate the SD cap.
const WIDEVINE_SD_ONLY_L3_LADDER: readonly WidevineRobustness[] =
  WIDEVINE_ROBUSTNESS_VALUES;

const WIDEVINE_HARDWARE_ROBUSTNESS_VALUES: readonly WidevineRobustness[] = [
  'HW_SECURE_ALL',
  'HW_SECURE_DECODE',
  'HW_SECURE_CRYPTO',
];

const WIDEVINE_SOFTWARE_ROBUSTNESS_VALUES: readonly WidevineRobustness[] = [
  'SW_SECURE_DECODE',
  'SW_SECURE_CRYPTO',
];

const WIDEVINE_CODEC_FAMILY_PRIORITY = [
  'av01',
  'hevc',
  'vp09',
  'avc1',
] as const;

export type ResolveWidevineRobustnessOptions = {
  explicitRobustness?: string | null;
  minimumSecurityLevel?: string | number | null;
  sdOnlyForL3: boolean;
  isSupported: (robustness: WidevineRobustness) => Promise<boolean>;
};

export type ResolvedWidevinePolicy = {
  videoRobustness: WidevineRobustness;
  restrictToSd: boolean;
};

export type WidevinePlaybackLevel = {
  videoCodec?: string;
  codecSet?: string;
  width?: number;
  height?: number;
  bitrate?: number;
};

export type ResolveWidevineCodecPolicyOptions = {
  levels: readonly WidevinePlaybackLevel[];
  maxSoftwareResolution: number | null;
  isSupported: (
    contentType: string,
    robustness: WidevineRobustness,
  ) => Promise<boolean>;
};

export type ResolvedWidevineCodecPolicy = ResolvedWidevinePolicy & {
  selectedCodecFamily: string | null;
  selectedVideoCodecs: string[];
  indicesToRemove: number[];
};

export function isWidevineRobustness(
  value: string | null | undefined,
): value is WidevineRobustness {
  return WIDEVINE_ROBUSTNESS_VALUES.includes(value as WidevineRobustness);
}

export function isSoftwareWidevineRobustness(
  value: WidevineRobustness | null,
): boolean {
  return value === 'SW_SECURE_DECODE' || value === 'SW_SECURE_CRYPTO';
}

export function requiresLegacyWidevineRequestDedupe(version: string): boolean {
  return /^1\.6(?:\.|$)/.test(version);
}

export function getWidevinePlaybackLevelIndicesToRemove(
  levels: readonly WidevinePlaybackLevel[],
  maxResolution: number,
): number[] {
  const indices: number[] = [];
  for (let index = 0; index < levels.length; index++) {
    const { width, height } = levels[index];
    const displayHeight = typeof width === 'number' && width > 0 &&
        typeof height === 'number' && height > 0
      ? Math.min(width, height)
      : typeof height === 'number' && height > 0
      ? height
      : null;

    if (displayHeight !== null && displayHeight > maxResolution) {
      indices.push(index);
    }
  }
  return indices;
}

function getWidevineLevelDisplayHeight(
  level: WidevinePlaybackLevel,
): number | null {
  const { width, height } = level;
  if (
    typeof width === 'number' && width > 0 &&
    typeof height === 'number' && height > 0
  ) {
    return Math.min(width, height);
  }
  return typeof height === 'number' && height > 0 ? height : null;
}

function getWidevineCodecFamily(level: WidevinePlaybackLevel): string | null {
  const codec = `${level.videoCodec ?? ''} ${level.codecSet ?? ''}`
    .toLowerCase();
  if (codec.includes('av01')) return 'av01';
  if (codec.includes('hvc1') || codec.includes('hev1')) return 'hevc';
  if (codec.includes('vp09') || codec.includes('vp9')) return 'vp09';
  if (codec.includes('avc1') || codec.includes('avc3')) return 'avc1';

  const prefix = level.videoCodec?.trim().toLowerCase().split('.')[0];
  return prefix || null;
}

export function getWidevineVideoContentType(
  videoCodec: string,
): string {
  return `video/mp4; codecs="${videoCodec}"`;
}

function getVideoCodecFromContentType(contentType: string): string | null {
  const match = /codecs\s*=\s*"?([^",;\s]+)/i.exec(contentType);
  return match?.[1]?.toLowerCase() ?? null;
}

export type WidevineCodecSupportMap = Map<
  WidevineRobustness,
  ReadonlySet<string>
>;

export async function probeWidevineCodecSupport(
  videoCodecs: readonly string[],
  requestAccess: (
    configurations: MediaKeySystemConfiguration[],
  ) => Promise<Pick<MediaKeySystemAccess, 'getConfiguration'>>,
  configurationTemplate: MediaKeySystemConfiguration = {
    initDataTypes: ['cenc'],
  },
): Promise<WidevineCodecSupportMap> {
  const uniqueCodecs = [
    ...new Set(
      videoCodecs.map((codec) => codec.trim()).filter(Boolean),
    ),
  ];
  const support = new Map<WidevineRobustness, ReadonlySet<string>>();
  const requestedCapabilities = configurationTemplate.videoCapabilities ?? [];

  for (const robustness of WIDEVINE_ROBUSTNESS_VALUES) {
    try {
      const access = await requestAccess([{
        ...configurationTemplate,
        videoCapabilities: uniqueCodecs.map((videoCodec) => {
          const requestedCapability = requestedCapabilities.find(
            ({ contentType }) =>
              contentType &&
              getVideoCodecFromContentType(contentType) ===
                videoCodec.toLowerCase(),
          );
          return {
            ...requestedCapability,
            contentType: getWidevineVideoContentType(videoCodec),
            robustness,
          };
        }),
      }]);
      const supportedCodecs = new Set<string>();
      for (
        const capability of access.getConfiguration().videoCapabilities ?? []
      ) {
        if (!capability.contentType) continue;
        const codec = getVideoCodecFromContentType(capability.contentType);
        if (codec) supportedCodecs.add(codec);
      }
      support.set(robustness, supportedCodecs);
    } catch (_error) {
      support.set(robustness, new Set());
    }
  }

  return support;
}

export function isWidevineCodecSupported(
  support: WidevineCodecSupportMap,
  contentType: string,
  robustness: WidevineRobustness,
): boolean {
  const codec = getVideoCodecFromContentType(contentType);
  return codec !== null && (support.get(robustness)?.has(codec) ?? false);
}

export function applyWidevineCodecPolicyToConfigurations(
  configurations: readonly MediaKeySystemConfiguration[],
  policy: ResolvedWidevineCodecPolicy,
): MediaKeySystemConfiguration[] {
  const selectedCodecs = new Set(
    policy.selectedVideoCodecs.map((codec) => codec.toLowerCase()),
  );
  if (selectedCodecs.size === 0) return [];

  return configurations.flatMap((configuration) => {
    const requestedCapabilities = configuration.videoCapabilities ?? [];
    const selectedCapabilities = requestedCapabilities
      .filter(({ contentType }) => {
        if (!contentType) return false;
        const codec = getVideoCodecFromContentType(contentType);
        return codec !== null && selectedCodecs.has(codec);
      })
      .map((capability) => ({
        ...capability,
        robustness: policy.videoRobustness,
      }));

    if (selectedCapabilities.length === 0) return [];
    return [{
      ...configuration,
      videoCapabilities: selectedCapabilities,
    }];
  });
}

type IndexedWidevineLevel = {
  index: number;
  level: WidevinePlaybackLevel;
  codecFamily: string;
  videoCodec: string;
  displayHeight: number | null;
};

type WidevineCodecCandidate = {
  codecFamily: string;
  robustness: WidevineRobustness;
  levels: IndexedWidevineLevel[];
  maxDisplayHeight: number;
};

function getWidevineCodecFamilyPriority(codecFamily: string): number {
  const priority = WIDEVINE_CODEC_FAMILY_PRIORITY.indexOf(
    codecFamily as (typeof WIDEVINE_CODEC_FAMILY_PRIORITY)[number],
  );
  return priority === -1 ? Number.MAX_SAFE_INTEGER : priority;
}

function compareWidevineCodecCandidates(
  left: WidevineCodecCandidate,
  right: WidevineCodecCandidate,
): number {
  if (left.maxDisplayHeight !== right.maxDisplayHeight) {
    return right.maxDisplayHeight - left.maxDisplayHeight;
  }

  const priorityDifference = getWidevineCodecFamilyPriority(
    left.codecFamily,
  ) - getWidevineCodecFamilyPriority(right.codecFamily);
  if (priorityDifference !== 0) {
    return priorityDifference;
  }

  return WIDEVINE_ROBUSTNESS_VALUES.indexOf(left.robustness) -
    WIDEVINE_ROBUSTNESS_VALUES.indexOf(right.robustness);
}

async function getSupportedWidevineRobustness(
  contentType: string,
  robustnessValues: readonly WidevineRobustness[],
  isSupported: ResolveWidevineCodecPolicyOptions['isSupported'],
): Promise<WidevineRobustness | null> {
  const weakest = robustnessValues.at(-1)!;
  if (!(await isSupported(contentType, weakest))) return null;

  for (const robustness of robustnessValues.slice(0, -1)) {
    if (await isSupported(contentType, robustness)) return robustness;
  }
  return weakest;
}

async function getWidevineCodecCandidate(
  levels: readonly IndexedWidevineLevel[],
  robustnessValues: readonly WidevineRobustness[],
  maxResolution: number | null,
  isSupported: ResolveWidevineCodecPolicyOptions['isSupported'],
  preferLowest = false,
): Promise<WidevineCodecCandidate | null> {
  const eligibleLevels = levels
    .filter(({ displayHeight }) =>
      maxResolution === null || displayHeight === null ||
      displayHeight <= maxResolution
    )
    .toSorted((left, right) => {
      const qualityDifference =
        (right.displayHeight ?? 0) - (left.displayHeight ?? 0) ||
        (right.level.bitrate ?? 0) - (left.level.bitrate ?? 0);
      return preferLowest ? -qualityDifference : qualityDifference;
    });

  for (const candidateLevel of eligibleLevels) {
    const robustness = await getSupportedWidevineRobustness(
      getWidevineVideoContentType(candidateLevel.videoCodec),
      robustnessValues,
      isSupported,
    );
    if (!robustness) continue;

    const selectedLevels: IndexedWidevineLevel[] = [];
    const codecSupport = new Map<string, boolean>();
    for (const level of eligibleLevels) {
      if (
        candidateLevel.displayHeight !== null && level.displayHeight !== null &&
        level.displayHeight > candidateLevel.displayHeight
      ) {
        continue;
      }

      let supported = codecSupport.get(level.videoCodec);
      if (supported === undefined) {
        supported = await isSupported(
          getWidevineVideoContentType(level.videoCodec),
          robustness,
        );
        codecSupport.set(level.videoCodec, supported);
      }
      if (supported) selectedLevels.push(level);
    }

    if (selectedLevels.length > 0) {
      return {
        codecFamily: candidateLevel.codecFamily,
        robustness,
        levels: selectedLevels,
        maxDisplayHeight: Math.max(
          ...selectedLevels.map(({ displayHeight }) => displayHeight ?? 0),
        ),
      };
    }
  }

  return null;
}

/**
 * Resolve SD-only-for-L3 against the codecs that are actually present in the
 * HLS manifest. Widevine robustness support is codec/profile-specific, so a
 * successful AVC probe must not authorize HD AV1, VP9, or HEVC renditions.
 */
export async function resolveWidevineCodecPolicy(
  options: ResolveWidevineCodecPolicyOptions,
): Promise<ResolvedWidevineCodecPolicy> {
  const indexedLevels = options.levels.flatMap((level, index) => {
    const codecFamily = getWidevineCodecFamily(level);
    const videoCodec = level.videoCodec?.trim();
    if (!codecFamily || !videoCodec) return [];
    return [{
      index,
      level,
      codecFamily,
      videoCodec,
      displayHeight: getWidevineLevelDisplayHeight(level),
    }];
  });
  const codecFamilies = [
    ...new Set(
      indexedLevels.map(({ codecFamily }) => codecFamily),
    ),
  ];

  const getCandidates = async (
    robustnessValues: readonly WidevineRobustness[],
    maxResolution: number | null,
    preferLowest = false,
  ): Promise<WidevineCodecCandidate[]> => {
    const candidates: WidevineCodecCandidate[] = [];
    for (const codecFamily of codecFamilies) {
      const candidate = await getWidevineCodecCandidate(
        indexedLevels.filter((level) => level.codecFamily === codecFamily),
        robustnessValues,
        maxResolution,
        options.isSupported,
        preferLowest,
      );
      if (candidate) candidates.push(candidate);
    }
    return candidates.toSorted((left, right) => {
      if (!preferLowest) {
        return compareWidevineCodecCandidates(left, right);
      }
      return left.maxDisplayHeight - right.maxDisplayHeight ||
        getWidevineCodecFamilyPriority(left.codecFamily) -
          getWidevineCodecFamilyPriority(right.codecFamily);
    });
  };

  const hardwareCandidates = await getCandidates(
    WIDEVINE_HARDWARE_ROBUSTNESS_VALUES,
    null,
  );
  const selected = hardwareCandidates[0] ?? (await getCandidates(
    WIDEVINE_SOFTWARE_ROBUSTNESS_VALUES,
    options.maxSoftwareResolution,
  ))[0] ?? (await getCandidates(
    WIDEVINE_SOFTWARE_ROBUSTNESS_VALUES,
    null,
    true,
  ))[0];

  if (!selected) {
    const fallbackLevel = indexedLevels.toSorted((left, right) =>
      (left.displayHeight ?? Number.MAX_SAFE_INTEGER) -
        (right.displayHeight ?? Number.MAX_SAFE_INTEGER) ||
      getWidevineCodecFamilyPriority(left.codecFamily) -
        getWidevineCodecFamilyPriority(right.codecFamily)
    )[0];
    if (fallbackLevel) {
      return {
        videoRobustness: 'SW_SECURE_CRYPTO',
        restrictToSd: true,
        selectedCodecFamily: fallbackLevel.codecFamily,
        selectedVideoCodecs: [fallbackLevel.videoCodec],
        indicesToRemove: options.levels.flatMap((_, index) =>
          index === fallbackLevel.index ? [] : [index]
        ),
      };
    }

    return {
      videoRobustness: 'SW_SECURE_CRYPTO',
      restrictToSd: true,
      selectedCodecFamily: null,
      selectedVideoCodecs: [],
      indicesToRemove: options.levels.map((_, index) =>
        index
      ),
    };
  }

  const selectedIndices = new Set(selected.levels.map(({ index }) => index));
  return {
    videoRobustness: selected.robustness,
    restrictToSd: isSoftwareWidevineRobustness(selected.robustness),
    selectedCodecFamily: selected.codecFamily,
    selectedVideoCodecs: [
      ...new Set(
        selected.levels.map(({ videoCodec }) => videoCodec),
      ),
    ],
    indicesToRemove: options.levels.flatMap((_, index) =>
      selectedIndices.has(index) ? [] : [index]
    ),
  };
}

function normalizeMinimumSecurityLevel(
  value: string | number | null | undefined,
): 1 | 2 | 3 | null {
  const parsed = typeof value === 'number' ? value : Number(value);
  return parsed === 1 || parsed === 2 || parsed === 3 ? parsed : null;
}

function robustnessForMinimumSecurityLevel(
  level: 1 | 2 | 3 | null,
): WidevineRobustness | null {
  switch (level) {
    case 1:
      return 'HW_SECURE_DECODE';
    case 2:
      return 'HW_SECURE_CRYPTO';
    case 3:
      return 'SW_SECURE_CRYPTO';
    default:
      return null;
  }
}

function configuredRobustness(
  explicitRobustness: string | null | undefined,
  minimumSecurityLevel: 1 | 2 | 3 | null,
): WidevineRobustness | null {
  const explicit = isWidevineRobustness(explicitRobustness)
    ? explicitRobustness
    : null;
  const minimum = robustnessForMinimumSecurityLevel(minimumSecurityLevel);

  // Without the L3 exception, L1/L2 is a hard policy. Do not allow an
  // inconsistent explicit EME value to weaken the minimum security level.
  if (minimumSecurityLevel === 1 || minimumSecurityLevel === 2) {
    if (!explicit) return minimum;

    const explicitIndex = WIDEVINE_ROBUSTNESS_VALUES.indexOf(explicit);
    const minimumIndex = WIDEVINE_ROBUSTNESS_VALUES.indexOf(minimum!);
    return explicitIndex <= minimumIndex ? explicit : minimum;
  }

  if (explicit) return explicit;

  return minimum;
}

export async function resolveWidevineRobustness(
  options: ResolveWidevineRobustnessOptions,
): Promise<ResolvedWidevinePolicy> {
  const minimumSecurityLevel = normalizeMinimumSecurityLevel(
    options.minimumSecurityLevel,
  );

  // Although minimum=L3 plus SD-only-for-L3 should not normally be emitted by
  // the backend, handle every SD-only combination defensively. Probe the real
  // device path from hardware to software so an L1/L2 device is not mistaken
  // for L3 merely because it can also satisfy a weaker software request.
  if (options.sdOnlyForL3) {
    for (const robustness of WIDEVINE_SD_ONLY_L3_LADDER) {
      if (await options.isSupported(robustness)) {
        return {
          videoRobustness: robustness,
          restrictToSd: isSoftwareWidevineRobustness(robustness),
        };
      }
    }

    return {
      videoRobustness: 'SW_SECURE_CRYPTO',
      restrictToSd: true,
    };
  }

  const requested = configuredRobustness(
    options.explicitRobustness,
    minimumSecurityLevel,
  );
  const requiresHardware = minimumSecurityLevel === 1 ||
    minimumSecurityLevel === 2;

  // Let requestMediaKeySystemAccess reject unsupported hardware. Falling back
  // here would admit an L3 client to content whose minimum is L1 or L2.
  if (requested && requiresHardware) {
    return { videoRobustness: requested, restrictToSd: false };
  }

  const candidates = requested
    ? WIDEVINE_ROBUSTNESS_VALUES.slice(
      WIDEVINE_ROBUSTNESS_VALUES.indexOf(requested),
    )
    : WIDEVINE_DETECTION_LADDER;

  for (const robustness of candidates) {
    if (await options.isSupported(robustness)) {
      return { videoRobustness: robustness, restrictToSd: false };
    }
  }

  return { videoRobustness: 'SW_SECURE_CRYPTO', restrictToSd: false };
}
